import * as THREE from 'three';

/**
 * 垂线计算工具集。
 * 职责：提供从外部点到直线、圆、圆弧、椭圆的垂足纯数学计算。
 * 说明：
 * 1. 所有计算在参考平面局部2D坐标系（THREE.Vector2）中进行；
 * 2. 调用方负责世界坐标 ↔ 局部坐标的转换；
 * 3. 所有函数均为纯函数，无副作用。
 */

/**
 * 计算外部点到直线的垂足（局部2D坐标）。
 * 数学原理：将 P-A 投影到方向向量 AB 上，得到垂足。
 * @param fromLocal 外部点局部2D坐标。
 * @param lineStartLocal 直线起点局部2D坐标。
 * @param lineEndLocal 直线终点局部2D坐标。
 * @returns 垂足局部2D坐标；直线退化为点时返回 null。
 */
export function computeLinePerpendicularFootLocal(
    fromLocal: THREE.Vector2,
    lineStartLocal: THREE.Vector2,
    lineEndLocal: THREE.Vector2,
): THREE.Vector2 | null {
    const ab = new THREE.Vector2().subVectors(lineEndLocal, lineStartLocal);
    const abLenSq = ab.lengthSq();
    if (abLenSq < 1e-18) {
        return null;
    }

    const ap = new THREE.Vector2().subVectors(fromLocal, lineStartLocal);
    // 投影参数 t（不夹紧，允许垂足在线段延长线上）。
    const t = ap.dot(ab) / abLenSq;
    return new THREE.Vector2(
        lineStartLocal.x + t * ab.x,
        lineStartLocal.y + t * ab.y,
    );
}

/**
 * 计算外部点到圆的垂足（局部2D坐标）。
 * 说明：圆的法线方向过圆心，垂足在 P→C 方向上距 P 点最近的圆上的点。
 * 即：从外部点出发，过圆心方向与圆的两个交点中，靠近外部点的那个。
 * @param fromLocal 外部点局部2D坐标。
 * @param centerLocal 圆心局部2D坐标。
 * @param radius 圆半径。
 * @returns 垂足局部2D坐标数组（圆心与外部点重合时返回空数组）。
 */
export function computeCirclePerpendicularFootsLocal(
    fromLocal: THREE.Vector2,
    centerLocal: THREE.Vector2,
    radius: number,
): THREE.Vector2[] {
    const dx = centerLocal.x - fromLocal.x;
    const dy = centerLocal.y - fromLocal.y;
    const d = Math.sqrt(dx * dx + dy * dy);

    if (d < 1e-9) {
        // 点在圆心，无确定方向。
        return [];
    }

    // 单位向量（P → C）。
    const ux = dx / d;
    const uy = dy / d;

    // 靠近外部点的垂足（沿 P→C 方向，距 P 为 d-r）。
    const nearFoot = new THREE.Vector2(fromLocal.x + (d - radius) * ux, fromLocal.y + (d - radius) * uy);
    // 远离外部点的垂足（沿 P→C 方向，距 P 为 d+r）。
    const farFoot = new THREE.Vector2(fromLocal.x + (d + radius) * ux, fromLocal.y + (d + radius) * uy);

    return [nearFoot, farFoot];
}

/**
 * 判断角度是否落在弧范围内（与切线计算模块相同逻辑，独立提供避免循环依赖）。
 * @param angle 待判断角度（弧度）。
 * @param startAngle 弧起始角（弧度）。
 * @param endAngle 弧终止角（弧度）。
 * @param clockwise 是否顺时针。
 * @returns 角度在弧范围内时返回 true。
 */
function isAngleInArcRange(
    angle: number,
    startAngle: number,
    endAngle: number,
    clockwise: boolean,
): boolean {
    const TWO_PI = Math.PI * 2;
    const normalize = (a: number): number => ((a % TWO_PI) + TWO_PI) % TWO_PI;
    const a = normalize(angle);
    const s = normalize(startAngle);
    const e = normalize(endAngle);

    if (clockwise) {
        if (s >= e) {
            return a <= s && a >= e;
        } else {
            return a <= s || a >= e;
        }
    } else {
        if (s <= e) {
            return a >= s && a <= e;
        } else {
            return a >= s || a <= e;
        }
    }
}

/**
 * 计算外部点到圆弧的垂足（局部2D坐标）。
 * 说明：先计算整圆垂足，再过滤出落在弧角度范围内的垂足。
 * @param fromLocal 外部点局部2D坐标。
 * @param centerLocal 弧圆心局部2D坐标。
 * @param radius 弧半径。
 * @param startAngle 弧起始角（弧度）。
 * @param endAngle 弧终止角（弧度）。
 * @param clockwise 是否顺时针。
 * @returns 垂足局部2D坐标数组（只包含落在弧范围内的垂足）。
 */
export function computeArcPerpendicularFootsLocal(
    fromLocal: THREE.Vector2,
    centerLocal: THREE.Vector2,
    radius: number,
    startAngle: number,
    endAngle: number,
    clockwise: boolean,
): THREE.Vector2[] {
    const allFoots = computeCirclePerpendicularFootsLocal(fromLocal, centerLocal, radius);
    return allFoots.filter((fp) => {
        const angle = Math.atan2(fp.y - centerLocal.y, fp.x - centerLocal.x);
        return isAngleInArcRange(angle, startAngle, endAngle, clockwise);
    });
}

/**
 * 计算外部点到椭圆的垂足（局部2D坐标）。
 * 数学原理：
 * 1. 将外部点变换到椭圆轴对齐坐标系；
 * 2. 椭圆上任意点 Q(t) = (a·cos(t), b·sin(t))，切向量 T(t) = (-a·sin(t), b·cos(t))；
 * 3. 垂足条件：(Q(t) - P) · T(t) = 0，展开得：
 *    (a·cos(t) - px)·(-a·sin(t)) + (b·sin(t) - py)·(b·cos(t)) = 0
 *    -a²·cos(t)·sin(t) + a·px·sin(t) + b²·sin(t)·cos(t) - b·py·cos(t) = 0
 *    (b²-a²)·sin(t)·cos(t) + a·px·sin(t) - b·py·cos(t) = 0
 *    (b²-a²)/2·sin(2t) + a·px·sin(t) - b·py·cos(t) = 0
 * 4. 用牛顿法迭代求解，取多个初始角均匀分布以覆盖所有垂足。
 * @param fromLocal 外部点局部2D坐标。
 * @param centerLocal 椭圆中心局部2D坐标。
 * @param a 椭圆长半轴（radiusX）。
 * @param b 椭圆短半轴（radiusY）。
 * @param rotation 椭圆长轴旋转角（弧度）。
 * @returns 垂足局部2D坐标数组。
 */
export function computeEllipsePerpendicularFootsLocal(
    fromLocal: THREE.Vector2,
    centerLocal: THREE.Vector2,
    a: number,
    b: number,
    rotation: number,
): THREE.Vector2[] {
    // 平移到椭圆中心为原点。
    const px0 = fromLocal.x - centerLocal.x;
    const py0 = fromLocal.y - centerLocal.y;

    // 旋转到椭圆轴对齐坐标系（旋转 -rotation）。
    const cosNeg = Math.cos(-rotation);
    const sinNeg = Math.sin(-rotation);
    const px = px0 * cosNeg - py0 * sinNeg;
    const py = px0 * sinNeg + py0 * cosNeg;

    // 垂足条件函数 f(t) 和导数 f'(t)。
    const f = (t: number): number => {
        const sinT = Math.sin(t);
        const cosT = Math.cos(t);
        return (b * b - a * a) * sinT * cosT + a * px * sinT - b * py * cosT;
    };
    const df = (t: number): number => {
        const sinT = Math.sin(t);
        const cosT = Math.cos(t);
        return (b * b - a * a) * (cosT * cosT - sinT * sinT) + a * px * cosT + b * py * sinT;
    };

    // 从均匀分布的初始角迭代求根，收集不重复的解。
    const INITIAL_COUNT = 8;
    const ITERATIONS = 20;
    const EPS = 1e-7;
    const DEDUP_EPS = 1e-4;

    const roots: number[] = [];
    for (let i = 0; i < INITIAL_COUNT; i++) {
        let t = (i / INITIAL_COUNT) * Math.PI * 2;
        // 牛顿法迭代。
        for (let iter = 0; iter < ITERATIONS; iter++) {
            const dfVal = df(t);
            if (Math.abs(dfVal) < 1e-15) {
                break;
            }
            const dt = f(t) / dfVal;
            t -= dt;
            if (Math.abs(dt) < EPS) {
                break;
            }
        }
        // 规范化到 [0, 2π)。
        t = ((t % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);

        // 过滤：f(t) 需接近 0。
        if (Math.abs(f(t)) > 1e-4) {
            continue;
        }
        // 去重。
        if (roots.some((r) => Math.abs(r - t) < DEDUP_EPS || Math.abs(r - t - Math.PI * 2) < DEDUP_EPS)) {
            continue;
        }
        roots.push(t);
    }

    // 旋转回参考平面局部坐标系的矩阵。
    const cosPos = Math.cos(rotation);
    const sinPos = Math.sin(rotation);

    return roots.map((t) => {
        // 椭圆上的点（轴对齐坐标系）。
        const tx = a * Math.cos(t);
        const ty = b * Math.sin(t);
        // 旋转回参考平面局部坐标系并加回中心偏移。
        return new THREE.Vector2(
            centerLocal.x + tx * cosPos - ty * sinPos,
            centerLocal.y + tx * sinPos + ty * cosPos,
        );
    });
}
